/**
 * Model Inputs State Management Interfaces
 * 
 * These interfaces support:
 * - N blends (scalable beyond Blend 1 and Blend 2)
 * - Dynamic columns (headers can change)
 * - Lazy creation (state is only created when needed)
 * - Smart merge with selection validation
 */

/**
 * Represents the current selection context for validation
 * Used to determine if saved data should be restored or fresh data loaded
 */
export interface SelectionContext {
  name: string;       // e.g., "Base Oil A", "VM 100"
  index: number;      // Position in dropdown (0-based)
}

/**
 * Represents a single section within Model Inputs
 * Each section (VM, Base Oil, PPD, Additives) has its own data and metadata
 */
export interface ModelInputSection {
  data: any[];              // The actual table data (rows)
  headers: string[];        // Column headers for this section
  savedSelections: SelectionContext[];  // Selections at time of save (for validation)
  isDirty: boolean;         // True if data has been modified since last save
  lastSaved: Date | null;   // Timestamp of last save (null if never saved)
}

/**
 * Creates an empty/default ModelInputSection
 * Used for lazy initialization
 */
export function createEmptySection(): ModelInputSection {
  return {
    data: [],
    headers: [],
    savedSelections: [],
    isDirty: false,
    lastSaved: null
  };
}

/**
 * Complete state for a single blend's Model Inputs
 * Contains all sections and metadata for one blend
 */
export interface ModelInputsBlendState {
  blendIndex: number;       // Which blend this state belongs to (1, 2, 3, etc.)
  vm: ModelInputSection;
  baseOil: ModelInputSection;
  ppd: ModelInputSection;
  additives: ModelInputSection;
  isInitialized: boolean;   // True once state has been populated
  lastModified: Date | null; // Last modification timestamp for any section
}

/**
 * Creates an empty/default ModelInputsBlendState for a given blend
 * Used for lazy initialization when a blend is first accessed
 */
export function createEmptyBlendState(blendIndex: number): ModelInputsBlendState {
  return {
    blendIndex,
    vm: createEmptySection(),
    baseOil: createEmptySection(),
    ppd: createEmptySection(),
    additives: createEmptySection(),
    isInitialized: false,
    lastModified: null
  };
}

/**
 * Section types enum for type-safe section access
 */
export type ModelInputSectionType = 'vm' | 'baseOil' | 'ppd' | 'additives';

/**
 * Payload for updating a specific section
 */
export interface SectionUpdatePayload {
  blendIndex: number;
  sectionType: ModelInputSectionType;
  data?: any[];
  headers?: string[];
  savedSelections?: SelectionContext[];
  markAsSaved?: boolean;
}

/**
 * Result of a merge operation
 * Indicates what action was taken when opening Model Inputs
 */
export interface MergeResult {
  action: 'restored' | 'fresh' | 'merged';
  sectionsRestored: ModelInputSectionType[];
  sectionsFresh: ModelInputSectionType[];
  message?: string;
}
